import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiGithub, FiExternalLink, FiArrowLeft, FiCalendar, FiEye } from 'react-icons/fi';
import { publicAPI } from '../services/api';
import { generateImageUrl, formatDate } from '../utils/helpers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getProjectBySlug(slug)
      .then(res => {
        if (res.success) {
          setProject(res.data);
          publicAPI.incrementProjectView(res.data.id).catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSpinner />;
  if (!project) return <div className="container"><h2>Project not found</h2></div>;

  return (
    <section className="section project-detail-page">
      <Helmet>
        <title>{project.title} | Deepak Chauhan</title>
        <meta name="description" content={project.shortDescription} />
        <meta property="og:title" content={project.title} />
        <meta property="og:description" content={project.shortDescription} />
        {project.thumbnailUrl && <meta property="og:image" content={generateImageUrl(project.thumbnailUrl)} />}
      </Helmet>
      <div className="container">
        <Link to="/projects" className="back-link"><FiArrowLeft /> Back to Projects</Link>

        <div className="project-detail">
          <div className="project-detail-image">
            <img src={generateImageUrl(project.thumbnailUrl)} alt={project.title} />
          </div>

          <div className="project-detail-content">
            <h1>{project.title}</h1>
            <div className="project-meta">
              <span><FiCalendar /> {formatDate(project.createdAt)}</span>
              <span><FiEye /> {project.viewCount} views</span>
              <span className={`status-badge ${project.status?.toLowerCase()}`}>{project.status}</span>
            </div>

            <div className="project-tech-list">
              {project.technologies?.split(',').map(t => (
                <span key={t} className="tech-badge">{t.trim()}</span>
              ))}
            </div>

            <p className="project-description">{project.description}</p>

            <div className="project-actions">
              {project.githubUrl && (
                <a href={project.githubUrl} target="_blank" rel="noreferrer" className="btn btn-primary">
                  <FiGithub /> View on GitHub
                </a>
              )}
              {project.liveDemoUrl && (
                <a href={project.liveDemoUrl} target="_blank" rel="noreferrer" className="btn btn-outline">
                  <FiExternalLink /> Live Demo
                </a>
              )}
            </div>
          </div>
        </div>

        {project.screenshots?.length > 0 && (
          <div className="project-screenshots">
            <h2>Screenshots</h2>
            <div className="screenshots-grid">
              {project.screenshots.map(s => (
                <div key={s.id} className="screenshot-item">
                  <img src={generateImageUrl(s.imageUrl)} alt={s.caption || 'Screenshot'} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectDetail;
